import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import {
	apiPaths,
	otherPartyPath,
	otherPartySchema,
	type OtherParty,
	type OtherPartyRequest,
} from "@ravnar/api-contract";

import { ApiError, apiRequest } from "./client";

// The Directory's "other" parties — the bank, the insurer, the investor. Every query key starts with "other-parties",
// so a change to one can refresh the kind on its own, while the Directory's own list is a projection under
// "directory" and is invalidated beside it.
//
// There is no reader for `GET /api/other-parties` here: the Directory lists parties off the projection, a page at a
// time, and a party's own page reads that one party. The route answers the whole list for the tests and for whatever
// asks next; a query nothing calls would only be an unused export, which `knip` refuses.

/** Adds an "other" party. Fails with 400 when the form's values are refused and 403 for anyone but an editor. */
export function addOtherParty(request: OtherPartyRequest): Promise<OtherParty> {
	return apiRequest(apiPaths.otherParties, otherPartySchema, { method: "POST", body: request });
}

/**
 * One "other" party of the organization, by id: what its page opens with, including its note. Only members with
 * access to the Directory may read it; the API answers everyone else with 403 and a party that is gone with 404.
 */
export const otherPartyQueryOptions = (partyId: string) =>
	queryOptions({
		queryKey: ["other-parties", "party", partyId],
		queryFn: ({ signal }) => apiRequest(otherPartyPath(partyId), otherPartySchema, { signal }),
		// A refused member or a party that is gone will not come back on a retry, and the page acts on it straight away.
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/**
 * Saves an "other" party's whole record, its note included. Fails with 400 when the values are refused, 403 for
 * anyone but an editor of the Directory, and 404 when the party is gone.
 */
export function updateOtherParty(partyId: string, request: OtherPartyRequest): Promise<OtherParty> {
	return apiRequest(otherPartyPath(partyId), otherPartySchema, { method: "PATCH", body: request });
}

/** Removes an "other" party for good; the audit log keeps what became of it. Fails with 403 and 404 as above. */
export async function removeOtherParty(partyId: string): Promise<void> {
	await apiRequest(otherPartyPath(partyId), z.undefined(), { method: "DELETE" });
}
